/**
 * LiveStreamModal — three-step wizard for scheduling a live broadcast.
 * Extracted from CreatorModals.tsx (AMB-232 slice 2). Behaviour
 * byte-for-byte identical — only the shared scaffolding was lifted to
 * creatorModalsShared.tsx and pulled in from there.
 *
 * Used by:
 *   - DashboardScreen (create new live + edit own announcement)
 *   - LiveDetailScreen (host's «Редактировать» action)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isModerationBlock, moderationRefusalMessage, shouldReportApiError } from '../../services/errorMessages';
import { captureError } from '../../utils/sentry';
import {
    View,
    Text,
    Modal,
    TouchableOpacity,
    TextInput,
    ScrollView,
    Image,
    ActivityIndicator,
    Platform,
    DeviceEventEmitter,
    useWindowDimensions,
} from 'react-native';
import { ON_ACCENT_TEXT } from '../../styles/accentText';
import Ionicons from '@expo/vector-icons/Ionicons';
import * as ImagePicker from 'expo-image-picker';

import { api, getShareOrigin, getFileUrl, unsplashPhotoUrl } from '../../services/api';
import { uploadCoverWithCeiling, CoverUploadTimeoutError } from './liveWizardCoverUpload';
import { COVER_LIST_TIMEOUT_MS, COVER_PHOTO_TIMEOUT_MS } from '../../screens/live-session/create/coverStepTimeouts';
import { storage } from '../../utils/storage';
import type { LiveAudienceTier, MasterLive, MasterVideo, UnsplashImage } from '../../types/auth';
import { parseDirections, serializeDirections } from '../../constants/directions';
import { useDirections } from '../../hooks/useDirections';
import { useI18n } from '../../i18n';
import { buildShareText, shareOrOpenModal, type ShareModalText } from '../../utils/roomShare';
import { ShareModal } from '../ShareModal';
import type { ShareTarget } from '../../services/shareAnalytics';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { feedAnnouncementSuppressed, announceSuccessBody, feedMessageHint } from './liveAnnouncementPlacement';
import { liveAudienceOptions, liveDirectionsRequired } from './liveAudienceOptions';
import { fontWeights } from '../../styles/typographyTokens';

import {
    ACCENT,
    fieldLabel,
    inputStyle,
    primaryBtn,
    orangeBtn,
    localISODate,
    localHHMM,
    todayISO,
    MIN_LIVE_LEAD_MINUTES,
    nextValidStart,
    isFutureStartWithBuffer,
    PUBLISH_DISCLAIMER,
    type LiveModalStep,
    formatPreviewDate,
} from './creatorModalsShared';

interface LiveStreamModalProps {
    visible: boolean;
    onClose: () => void;
    onCreated?: (live: MasterLive) => void;
    isDark: boolean;
    colors: any;
    /** When set, modal opens in edit mode and PUT-s instead of POST-ing. */
    existingLive?: MasterLive | null;
    /** When true together with `existingLive`, modal prefills fields from
     *  the source event but treats submission as a fresh create — POSTing
     *  instead of PUTting. The default start date bumps to +7 days so the
     *  master gets a sensible "next week, same time" suggestion. Used by
     *  the «Пересоздать на новую дату» action on archive announcements. */
    isRecreate?: boolean;
    /** Master display name, shown in announcement preview. */
    masterName?: string;
    /** Optional navigation handle — enables "Open event chat" shortcut on success step. */
    navigation?: any;
}

export const LiveStreamModal: React.FC<LiveStreamModalProps> = ({
    visible, onClose, onCreated, isDark, colors: rawColors, existingLive, isRecreate, masterName, navigation,
}) => {
    // Callers pass either the Dashboard-style `tc` (has `text` / `textSec`)
    // or the global ThemeContext `colors` (`textPrimary` / `textSecondary`).
    // The modal — and `creatorModalsShared` — uses the short names. Without
    // this normalization, `tc.text` is `undefined` on dark theme when opened
    // from LiveDetailScreen → RN falls back to black, so the entire form
    // (and the «Просмотр анонса» preview step) renders black-on-graphite.
    const tc: any = {
        ...rawColors,
        text: rawColors?.text ?? rawColors?.textPrimary,
        textSec: rawColors?.textSec ?? rawColors?.textSecondary,
        textMuted: rawColors?.textMuted,
    };
    // Recreate mode prefills from existingLive but submits as a fresh
    // create — don't treat the source as the target of the mutation.
    const isEdit = !!existingLive && !isRecreate;
    const { t } = useI18n('common');
    // AMB-11: cohort/cohost invites are gated behind a server-side feature
    // flag while we stabilise the multi-speaker pipeline. Flag OFF on dev
    // and prod by default. When off, the «Соло / С гостями» picker is
    // hidden (format locked to solo) and the success-step invite block
    // is suppressed. The publisher-side «Пригласить» button has the same
    // gate in MasterLivePublisher.
    const featureFlags = useFeatureFlags();
    const cohostsEnabled = featureFlags.cohosts === true;
    const [step, setStep] = useState<LiveModalStep>('edit');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [directions, setDirections] = useState<string[]>([]);
    const directionOptions = useDirections();
    const { width: viewportWidth } = useWindowDimensions();
    const isMobile = viewportWidth < 768;
    // Smart default — next 15-min mark with the live-broadcast lead-time
    // buffer so the prefilled value is already submit-valid. Master can
    // still pick a later slot from the picker.
    const liveInitialStart = useMemo(() => nextValidStart(MIN_LIVE_LEAD_MINUTES), []);
    const [date, setDate] = useState(liveInitialStart.date);
    const [time, setTime] = useState(liveInitialStart.time);
    const [duration, setDuration] = useState('60');
    const [isRegular, setIsRegular] = useState(false);
    // CORE-505 — «Кто увидит Live-сессию»: 3-level visibility (all/subscribers/private),
    // mirroring the native LiveWizard + VideoPractice/WatchParty tiers. This used to
    // be force-set to public on web (QA CORE-505: the selector was missing entirely,
    // so a master's «только подписчики / по приглашению» choice couldn't be made).
    // Default 'all' keeps the historical public-by-default behaviour for masters who
    // don't touch the control.
    // CORE-879 — четвёртый уровень, `program`, у Live-сессии из платной программы:
    // выбрать его здесь нельзя (такая сессия заводится в редакторе программы),
    // но при правке он обязан сохраниться и быть виден.
    const [visibility, setVisibility] = useState<LiveAudienceTier>('all');
    // Multi-speaker live (interview format). 'solo' = master alone, 'with-guests' = master + 1..4 guests.
    // guestCount = number of guests (1..4), maps directly to coHostMaxSlots.
    const [liveFormat, setLiveFormat] = useState<'solo' | 'with-guests'>('solo');
    const [guestCount, setGuestCount] = useState<1 | 2 | 3 | 4>(1);
    const [coverFile, setCoverFile] = useState<any>(null);
    const [coverPreview, setCoverPreview] = useState<string | null>(null);
    // Live events are always public — co-host invitations are issued from the
    // owner's preview/broadcast room (MasterLivePublisher). Visibility is no
    // longer a creation-time choice.
    const [circleMessage, setCircleMessage] = useState('');
    const [unsplashCovers, setUnsplashCovers] = useState<UnsplashImage[]>([]);
    const [coversLoading, setCoversLoading] = useState(false);
    // CORE-1994 — the gallery and a pick report their failure instead of
    // swallowing it: Unsplash's image host does not answer from Russia, and the
    // old empty catch left the master with a «chosen» cover that never loaded.
    const [coversFailed, setCoversFailed] = useState(false);
    const [pickingCoverId, setPickingCoverId] = useState<string | null>(null);
    const [coverError, setCoverError] = useState<string | null>(null);
    const [coverRetry, setCoverRetry] = useState<{ run: () => void } | null>(null);
    const [createdLive, setCreatedLive] = useState<MasterLive | null>(null);
    // CORE-740 — whether the just-published live fell inside the backend's
    // 2-hour lead gate, so the success copy stops promising a feed card the
    // gate withholds. Captured at publish time to match what the BE decided.
    const [feedSuppressed, setFeedSuppressed] = useState(false);
    const [publishing, setPublishing] = useState(false);
    // CORE-522 — synchronous re-entrancy guard for handlePublish. The button's
    // `disabled={publishing}` and a state-based guard both rely on a React
    // re-render, so a fast double-tap (two touches before commit) can slip
    // through and POST createLive twice. A ref flips synchronously, so the
    // second tap returns immediately → «session created only once».
    const publishingRef = useRef(false);
    // CORE-1169 — тело модалки прокручивается, и при переходе на следующий шаг
    // оно оставалось там, где его бросили: новый шаг открывался с середины.
    const bodyRef = useRef<ScrollView>(null);
    const [error, setError] = useState<string | null>(null);
    // AMB-24 — optional link to a VideoPractice from the master's library.
    // When set, the BE attaches it to the live row; on the FE we auto-fill
    // title/cover from the practice and surface «Привязанная практика» on
    // the detail page. Stored as the full picked item so we can pre-fill
    // ui-side fields without an extra round-trip.
    const [linkedPractice, setLinkedPractice] = useState<MasterVideo | null>(null);
    const [practiceLibrary, setPracticeLibrary] = useState<MasterVideo[]>([]);
    const [practiceLibraryLoading, setPracticeLibraryLoading] = useState(false);
    const [practicePickerOpen, setPracticePickerOpen] = useState(false);
    const [practiceSearch, setPracticeSearch] = useState('');
    // Invite link generated on success-step for with-guests events (mic+cam).
    const [inviteLink, setInviteLink] = useState<string | null>(null);
    const [inviteError, setInviteError] = useState<string | null>(null);
    // CORE-786 — which link the «Поделиться» popup is open for.
    const [shareFor, setShareFor] = useState<'invite' | 'viewer' | null>(null);

    // CORE-740 — live preview of whether the chosen start falls inside the
    // backend's 2-hour feed-announcement gate, so the confirm-step composer
    // hint is honest before the master hits «Опубликовать». Edit mode keeps the
    // legacy copy (the announcement may already exist / be rescheduled).
    const confirmFeedSuppressed = useMemo(() => {
        if (isEdit) return false;
        const startISO = new Date(`${date}T${time}:00`).toISOString();
        return feedAnnouncementSuppressed(startISO, Date.now());
    }, [isEdit, date, time]);

    const toggleDirection = (d: string) => {
        setDirections((prev) => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
    };

    const reset = () => {
        setStep('edit');
        setTitle(''); setDescription(''); setDirections([]);
        const start = nextValidStart(MIN_LIVE_LEAD_MINUTES);
        setDate(start.date);
        setTime(start.time); setDuration('60'); setIsRegular(false);
        setCoverFile(null); setCoverPreview(null);
        setPickingCoverId(null); setCoverError(null); setCoverRetry(null);
        setCircleMessage('');
        setCreatedLive(null); setFeedSuppressed(false);
        setError(null); setPublishing(false); publishingRef.current = false;
        setVisibility('all');
        setLiveFormat('solo'); setGuestCount(1);
        setInviteLink(null); setInviteError(null); setShareFor(null);
        setLinkedPractice(null); setPracticePickerOpen(false); setPracticeSearch('');
    };

    // CORE-1994 — the list has a ceiling (Live's CORE-1597 value); a failed or
    // stalled list says so and offers «Повторить» instead of spinning forever.
    const loadCovers = React.useCallback(() => {
        setCoversLoading(true);
        setCoversFailed(false);
        uploadCoverWithCeiling(api.getUnsplashLiveCovers(), COVER_LIST_TIMEOUT_MS)
            .then(list => setUnsplashCovers(Array.isArray(list) ? list : []))
            .catch(() => { setUnsplashCovers([]); setCoversFailed(true); })
            .finally(() => setCoversLoading(false));
    }, []);

    React.useEffect(() => {
        if (!visible) return;
        // Lazy-load Unsplash suggestions on every open (cheap public endpoint).
        loadCovers();

        if (existingLive) {
            // For recreate, suggest «same time, +7 days» as a sensible
            // default — the master can still pick another date in the
            // form. Cover is cleared because createLive expects a File,
            // not a URL — the master re-picks (or omits) on submit.
            const sourceDate = new Date(existingLive.startDateTimeUtc);
            const startDate = isRecreate
                ? new Date(sourceDate.getTime() + 7 * 24 * 60 * 60 * 1000)
                : sourceDate;
            setTitle(existingLive.title || '');
            setDescription(existingLive.description || '');
            setDirections(parseDirections(existingLive.category));
            // Use LOCAL date/time helpers — `.toISOString().slice(0,10)`
            // returns UTC date which silently rolls ±1 day for events near
            // local midnight. The pencil-edit on an evening announcement
            // would hydrate with «day later» before this fix.
            setDate(localISODate(startDate));
            setTime(localHHMM(startDate));
            setDuration(String(existingLive.durationMinutes ?? 60));
            setIsRegular(!!existingLive.isRegular);
            // CORE-505 — hydrate the chosen visibility tier on edit/recreate.
            setVisibility(existingLive.visibility ?? 'all');
            setCoverPreview(isRecreate ? null : (existingLive.coverImageUrl ? existingLive.coverImageUrl : null));
            setCoverFile(null);
            setStep('edit');
            // AMB-24 — hydrate the linked-practice chip on edit so the
            // master sees what's attached. Recreate intentionally drops
            // the link (the new event might not be about the same
            // practice; user can re-attach via the picker).
            if (!isRecreate && existingLive.videoPracticeId) {
                setLinkedPractice({
                    id: existingLive.videoPracticeId,
                    title: existingLive.videoPracticeTitle || existingLive.title,
                    contentType: 'practice',
                    publishedAtUtc: null,
                    durationMinutes: existingLive.videoPracticeDurationMinutes ?? null,
                    playbackUrl: existingLive.videoPracticePlaybackUrl ?? null,
                    playbackUrl480p: existingLive.videoPracticePlaybackUrl480p ?? null,
                    coverImageUrl: existingLive.videoPracticeCoverImageUrl ?? null,
                    category: null,
                    isHidden: false,
                });
            }
            // Hydrate format/guests from BE field (post-SCRUM-28) or FE storage fallback.
            // Picker for guest count was removed 2026-05-07 — format is now binary
            // (solo / with-guest), guest count is always 1. If we're editing an old
            // multi-guest live (coHostMaxSlots > 1), it collapses to 1 here; the
            // downsize-confirm in handlePublish will warn before saving.
            const initialMax = existingLive.coHostMaxSlots ?? null;
            if (initialMax != null) {
                setLiveFormat('with-guests');
                setGuestCount(1);
            } else {
                setLiveFormat('solo');
                setGuestCount(1);
                storage.getLocalCohortMax(existingLive.id).then(saved => {
                    if (saved != null) {
                        setLiveFormat('with-guests');
                        setGuestCount(1);
                    }
                });
            }
        } else {
            reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, existingLive?.id]);

    // AMB-24 — lazy-load the master's video library when the modal opens so
    // the practice picker is populated as soon as the user hits «Выбрать
    // практику». Cheap call (small list per master).
    React.useEffect(() => {
        if (!visible) { setPracticeLibrary([]); return; }
        let cancelled = false;
        setPracticeLibraryLoading(true);
        api.getMasterVideos()
            .then(list => {
                if (cancelled) return;
                const published = (list || []).filter(v => !v.isHidden && v.publishedAtUtc);
                setPracticeLibrary(published);
            })
            .catch(() => { if (!cancelled) setPracticeLibrary([]); })
            .finally(() => { if (!cancelled) setPracticeLibraryLoading(false); });
        return () => { cancelled = true; };
    }, [visible]);

    // AMB-24 — auto-fill title / cover / duration from the linked practice
    // on first attach. Empty title and «60»-default duration are the
    // sentinels for «master hasn't customized this yet» — we don't
    // clobber values the user already typed.
    React.useEffect(() => {
        if (!linkedPractice) return;
        if (!title || title.trim().length === 0) {
            setTitle(linkedPractice.title || '');
        }
        if (!description || description.trim().length === 0) {
            const cat = linkedPractice.category || '';
            setDescription(cat ? `Практика: ${cat}` : '');
        }
        if (linkedPractice.durationMinutes && (!duration || duration === '60')) {
            setDuration(String(linkedPractice.durationMinutes));
        }
        if (linkedPractice.coverImageUrl && !coverPreview) {
            // Preview the practice cover so the master sees the prefill;
            // the actual coverFile stays null — BE will reuse the practice
            // cover if the master doesn't replace it.
            setCoverPreview(getFileUrl(linkedPractice.coverImageUrl) || null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedPractice]);

    // Generate the invite link the moment the user lands on the success step
    // (with-guests mode only). Solo mode skips this entirely.
    React.useEffect(() => {
        if (step !== 'success') return;
        if (liveFormat !== 'with-guests' || !createdLive?.id) return;
        // AMB-11: don't pull an invite token when the cohorts feature is off.
        // Defensive — picker is already hidden when the flag is off, but
        // an edit-flow live with `coHostMaxSlots > 0` from before could
        // still hit this path.
        if (!cohostsEnabled) return;
        let cancelled = false;
        (async () => {
            try {
                const res: any = await api.createLivePreviewToken(createdLive.id);
                if (cancelled) return;
                const token: string = res?.token || res?.previewToken || '';
                if (!token) throw new Error('no token returned');
                setInviteLink(`${getShareOrigin()}/live/${createdLive.id}?preview=${encodeURIComponent(token)}`);
                setInviteError(null);
            } catch {
                if (cancelled) return;
                setInviteError('Не удалось создать ссылку, перейдите в предпросмотр Live-сессии и нажмите «Пригласить гостя»');
            }
        })();
        return () => { cancelled = true; };
    }, [step, liveFormat, createdLive?.id, cohostsEnabled]);

    const handleClose = () => { reset(); onClose(); };

    const handlePickCover = async () => {
        const res = await ImagePicker.launchImageLibraryAsync({
            mediaTypes: ['images'], allowsEditing: true, aspect: [16, 9], quality: 0.85,
        });
        if (!res.canceled && res.assets[0]) {
            const asset = res.assets[0];
            setCoverPreview(asset.uri);
            if (Platform.OS === 'web') {
                const blob = await fetch(asset.uri).then(r => r.blob());
                setCoverFile(new File([blob], 'cover.jpg', { type: blob.type || 'image/jpeg' }));
            } else {
                setCoverFile({ uri: asset.uri, name: 'cover.jpg', type: 'image/jpeg' } as any);
            }
        }
    };

    // CORE-1994 — the photo comes through our API (`unsplashPhotoUrl`, CORE-1545),
    // never from Unsplash's image host: it times out from Russia. The pick has a
    // ceiling; the cover is shown as chosen only once the photo arrived, and a
    // failure keeps the previous cover (e.g. the Live's current one on edit) and
    // offers «Повторить».
    const handlePickUnsplash = async (img: UnsplashImage) => {
        if (pickingCoverId) return;
        const regularUrl = unsplashPhotoUrl(img.id, 'regular');
        setPickingCoverId(img.id);
        setCoverError(null);
        setCoverRetry(null);
        try {
            // Fetch the regular image, convert to File for upload, track download.
            const blob = await uploadCoverWithCeiling((async () => {
                // CORE-1997 — no-store: the <img> preview's cached copy has no CORS headers.
                const res = await fetch(regularUrl, { cache: 'no-store' });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.blob();
            })(), COVER_PHOTO_TIMEOUT_MS);
            if (Platform.OS === 'web') {
                setCoverFile(new File([blob], `unsplash-${img.id}.jpg`, { type: blob.type || 'image/jpeg' }));
            } else {
                setCoverFile({ uri: regularUrl, name: `unsplash-${img.id}.jpg`, type: 'image/jpeg' } as any);
            }
            setCoverPreview(regularUrl);
            api.trackUnsplashDownload(img.downloadLocationUrl).catch(() => { /* non-fatal */ });
        } catch (e) {
            setCoverError(e instanceof CoverUploadTimeoutError
                ? e.message
                : 'Не получилось взять эту обложку. Попробуйте ещё раз, выберите другую или загрузите свою.');
            setCoverRetry({ run: () => { void handlePickUnsplash(img); } });
        } finally {
            setPickingCoverId(null);
        }
    };

    const CARD_BG = isDark ? '#162130' : '#FFFFFF';
    // CORE-879 — у сессии программы направление необязательно: в общий
    // календарь она не попадает, а редактор программы его не спрашивает.
    // Правило одно на кнопку, на «Далее», на звёздочку и на красную плашку.
    const directionsRequired = liveDirectionsRequired(visibility);
    const canPreview = title.trim().length >= 3 && (!directionsRequired || directions.length > 0) && !publishing;

    // CORE-1169 — каждый шаг начинается сверху. Без этого «Далее» со дна
    // первого шага открывало второй сразу с середины.
    useEffect(() => { bodyRef.current?.scrollTo({ y: 0, animated: false }); }, [step]);

    // AMB-197 — per-field validation with actionable messages. Button is
    // visually disabled when canPreview=false but still receives presses,
    // so testers get explicit feedback instead of a frozen-looking control.
    const handleGoToPreview = () => {
        if (publishing) return;
        if (title.trim().length < 3) {
            setError('Введите название Live-сессии — минимум 3 символа.');
            return;
        }
        if (directionsRequired && directions.length === 0) {
            setError('Выберите хотя бы одно направление — иначе событие не попадёт в фильтры календаря.');
            return;
        }
        if (!isFutureStartWithBuffer(date, time, MIN_LIVE_LEAD_MINUTES)) {
            setError(`Live-сессия должна начаться минимум через ${MIN_LIVE_LEAD_MINUTES} минут от текущего момента — гостям нужно время открыть ссылку и подготовить камеру с микрофоном.`);
            return;
        }
        setError(null);
        // Leave circleMessage empty when the master hasn't typed anything —
        // see the matching comment in the offline-event handler above. Same
        // contract: the backend event-card poster uses text=title fallback +
        // filters out text===title, so no auto-boilerplate appears as a
        // quoted-line over the event card.
        setStep('preview');
    };

    const handlePublish = async () => {
        // CORE-522 — bail if a publish is already in flight (first await below is
        // an async yield, so a second tap could otherwise re-enter and double-
        // create). Flag flips synchronously; reset in finally.
        if (publishingRef.current) return;
        publishingRef.current = true;
        // Capture the previous cohort cap BEFORE any storage writes — used
        // both for the downsize-confirm dialog and for token-regenerate
        // detection on with-guests → solo.
        const prevMax = (isEdit && existingLive)
            ? await storage.getLocalCohortMax(existingLive.id)
            : null;

        // Edit-mode downsize confirm: if existingLive's previously-saved cap was
        // higher than the new pick, ask before proceeding. We only have the
        // master's previous cap from FE storage (until BE follow-up SCRUM-28
        // ships); guests-already-joined data isn't available here.
        if (isEdit) {
            const newMax = liveFormat === 'with-guests' ? guestCount : null;
            if (prevMax != null && newMax != null && newMax < prevMax) {
                const ok = (Platform.OS === 'web' && typeof window !== 'undefined')
                    ? window.confirm(`Сейчас лимит — ${prevMax} ${prevMax === 1 ? 'гость' : 'гостя'}. Новый лимит — ${newMax}. Гостей сверх лимита не отключаем, но новые по ссылке зайти не смогут. Продолжить?`)
                    : true;
                if (!ok) { publishingRef.current = false; return; }
            }
        }
        setPublishing(true); setError(null);
        try {
            const startISO = new Date(`${date}T${time}:00`).toISOString();
            // CORE-740 — record whether the backend's 2-hour lead gate will
            // withhold the feed announcement, so the success copy matches
            // reality instead of always promising «появится в вашей ленте».
            setFeedSuppressed(!isEdit && feedAnnouncementSuppressed(startISO, Date.now()));
            const coHostMaxSlots = liveFormat === 'with-guests' ? guestCount : undefined;
            const payload: any = {
                title: title.trim(),
                startDateTimeUtc: startISO,
                durationMinutes: parseInt(duration, 10) || 60,
                description: description.trim() || undefined,
                // CORE-331 — personal invitation («Сообщение в вашу ленту»)
                // becomes the feed announcement body server-side.
                feedMessage: circleMessage.trim() || undefined,
                category: serializeDirections(directions) || undefined,
                categories: directions.length > 0 ? directions : undefined,
                isRegular,
                // CORE-505 — send the chosen visibility tier (all/subscribers/private).
                // BE maps 'private' → IsPrivate (+ preview-token invite join) and gates
                // 'subscribers' in listings/feed/join to the master's subscribers.
                visibility,
                coverImage: coverFile || undefined,
                coHostMaxSlots,
                // AMB-24 — send `videoPracticeId` when the chip is set;
                // on edit, if the master detached an existing attachment
                // send `0` (BE's «clear» sentinel). Omit otherwise so
                // BE leaves the stored value untouched.
                videoPracticeId: linkedPractice
                    ? linkedPractice.id
                    : (isEdit && existingLive?.videoPracticeId ? 0 : undefined),
            };
            const result = isEdit
                ? await api.updateLive(existingLive!.id, payload)
                : await api.createLive(payload);
            // CORE-505 — visibility is now an explicit creation choice (the
            // «Кто увидит Live-сессию» selector), sent in the payload above; we no
            // longer force the live public here. `isHidden` (unlist) stays
            // orthogonal to the tier and is left untouched.
            setCreatedLive(result);
            // FE-fallback for the cohort cap: until BE follow-up SCRUM-28 ships,
            // the master's chosen N is only visible on this device.
            if (coHostMaxSlots != null) {
                await storage.setLocalCohortMax(result.id, coHostMaxSlots);
            } else {
                await storage.removeLocalCohortMax(result.id);
            }
            // Spec D-10: switching with-guests → solo invalidates the existing
            // invite token by regenerating it. The new token is never surfaced
            // here — its only purpose is to make the old URL stop working.
            if (isEdit && existingLive && prevMax != null && coHostMaxSlots == null) {
                try {
                    await api.createLivePreviewToken(existingLive.id);
                } catch {
                    // non-fatal: master can manually regenerate from the publisher
                }
            }
            // Notify calendar / starting-soon listeners so the newly-created
            // live shows up on the home page DailyRhythm, LivesScreen, the
            // StartingSoon strip and MyCalendarPanel without a hard refresh.
            if (!isEdit) {
                DeviceEventEmitter.emit('live_published');
            } else {
                DeviceEventEmitter.emit('live_updated');
            }
            onCreated?.(result);
            setStep('success');
        } catch (e: any) {
            // A content-moderation block (BE 422) returns a raw English reason;
            // show the branded RU copy instead of leaking it into the form.
            const moderation = isModerationBlock(e);
            // CORE-308 — report real publish failures (network / 5xx / bad
            // payload); a moderation block is an expected content rejection.
            // CORE-1099 — модерация была лишь частным случаем: любой отказ
            // бэкенда по содержимому формы мастер читает в `setError` ниже.
            if (!moderation && shouldReportApiError(e)) {
                captureError(e, { tags: { flow: 'live_create', status: e?.status } });
            }
            setError(moderation ? moderationRefusalMessage(e) : (e?.message || 'Не удалось опубликовать событие'));
        } finally {
            setPublishing(false);
            publishingRef.current = false;
        }
    };

    // Viewer link = clean URL (no preview-token) — for the audience that just
    // watches and chats.
    const viewerLink = createdLive?.id != null
        ? `${getShareOrigin()}/live/${createdLive.id}`
        : null;

    // CORE-786 — one «Поделиться» per link; a phone gets the OS sheet, a
    // desktop the popup. The viewer link uses the localized Live template
    // (CORE-743); the co-host invite carries its own sentence and the preview
    // token, so it is not an entity URL.
    const liveTitle = title.trim() || createdLive?.title || '';
    const shareTexts: Record<'invite' | 'viewer', ShareModalText | null> = {
        invite: inviteLink
            ? {
                url: inviteLink,
                title: `Приглашаю тебя как соведущего на «${liveTitle}»`,
                message: `Приглашаю тебя как соведущего на «${liveTitle}»\n${inviteLink}`,
            }
            : null,
        viewer: createdLive?.id != null
            ? (() => {
                const text = buildShareText({ type: 'live', id: createdLive.id, title: liveTitle });
                return { url: text.url, title: text.subject, subject: text.subject, message: text.message };
            })()
            : null,
    };
    const shareTarget: ShareTarget = { entityType: 'live', entityId: createdLive?.id, surface: 'live_detail' };
    const openShare = (which: 'invite' | 'viewer') => {
        const text = shareTexts[which];
        if (!text) return;
        void shareOrOpenModal(text, shareTarget, () => setShareFor(which));
    };

    // Heuristic: MasterLive has no status field. We approximate using
    // playbackUrl (set when ended) and start time + duration. Matches the
    // pattern used elsewhere in the codebase (LiveDetailScreen reads BE's
    // `status` directly because LiveDetailResponse has it).
    const liveStatus: 'upcoming' | 'live' | 'ended' = (() => {
        if (!existingLive) return 'upcoming';
        if (existingLive.playbackUrl) return 'ended';
        const startMs = new Date(existingLive.startDateTimeUtc).getTime();
        const nowMs = Date.now();
        const durationMs = (existingLive.durationMinutes ?? 60) * 60 * 1000;
        if (nowMs >= startMs && nowMs < startMs + durationMs) return 'live';
        if (nowMs >= startMs + durationMs) return 'ended';
        return 'upcoming';
    })();
    const formatControlsDisabled = liveStatus === 'live';
    // AMB-11: also hide the format picker when the cohosts flag is off —
    // solo is the only available format in that state.
    const formatControlsHidden = liveStatus === 'ended' || !cohostsEnabled;

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
            <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                <View style={{ width: '100%', maxWidth: 880, maxHeight: '92%', backgroundColor: CARD_BG, borderRadius: 22, overflow: 'hidden', ...(Platform.OS === 'web' && { boxShadow: '0 20px 60px rgba(0,0,0,0.25)' } as any) }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 26, paddingTop: 24, paddingBottom: 14 }}>
                        <Text style={{ fontSize: 24, fontWeight: fontWeights.bold, color: tc.text } as any}>
                            {step === 'success'
                                ? (isEdit ? 'Изменения сохранены' : 'Событие опубликовано')
                                : (isRecreate ? 'Пересоздать на новую дату' : (isEdit ? 'Редактировать Live-сессию' : 'Запланировать Live-сессию'))}
                        </Text>
                        <TouchableOpacity onPress={handleClose} style={{ width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' }}>
                            <Ionicons name="close" size={22} color={tc.text} />
                        </TouchableOpacity>
                    </View>

                    {/* CORE-1169 — тело прокручивается и СЖИМАЕТСЯ: место под футер
                        с действием забирается у него, а не выталкивает футер за
                        нижнюю кромку окна. `overscrollBehavior` держит колесо
                        внутри модалки — иначе на её краю прокрутка перескакивала
                        на страницу под затемнением. */}
                    <ScrollView
                        ref={bodyRef}
                        style={{ flexShrink: 1, paddingHorizontal: 26, ...(Platform.OS === 'web' ? ({ overscrollBehavior: 'contain' } as any) : null) }}
                        contentContainerStyle={{ paddingBottom: 26 }}
                    >
                        {step === 'edit' && (
                            <>
                                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 24 } as any}>
                                    {/* ── LEFT COLUMN ── */}
                                    <View style={{ flex: 1, minWidth: 320 } as any}>
                                        <Text style={fieldLabel(tc.text) as any}>Название события <Text style={{ color: '#EF4444' }}>*</Text></Text>
                                        <TextInput value={title} onChangeText={setTitle} placeholder="напр. Коллективное пробуждение" placeholderTextColor={tc.textMuted} style={[inputStyle(isDark, tc), { marginBottom: 18 }] as any} />

                                        <Text style={fieldLabel(tc.text) as any}>Описание</Text>
                                        <TextInput value={description} onChangeText={setDescription} placeholder="О чём будет Live-сессия?" placeholderTextColor={tc.textMuted} multiline style={[inputStyle(isDark, tc), { minHeight: 110, textAlignVertical: 'top', marginBottom: 18 }] as any} />

                                        <Text style={fieldLabel(tc.text) as any}>Направления {directionsRequired && <Text style={{ color: '#EF4444' }}>*</Text>}</Text>
                                        <Text style={{ fontSize: 12, color: tc.textMuted, marginTop: -4, marginBottom: 8, lineHeight: 17 } as any}>
                                            {directionsRequired
                                                ? 'Выберите одно или несколько — событие появится во всех соответствующих фильтрах календаря.'
                                                : 'Необязательно: Live-сессия программы в общий календарь не попадает, её видят только купившие.'}
                                        </Text>
                                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
                                            {directionOptions.map((d) => {
                                                const active = directions.includes(d);
                                                return (
                                                    <TouchableOpacity
                                                        key={d}
                                                        onPress={() => toggleDirection(d)}
                                                        activeOpacity={0.85}
                                                        style={{ paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999, backgroundColor: active ? ACCENT : 'transparent', borderWidth: 1, borderColor: active ? ACCENT : (isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)'), ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}
                                                    >
                                                        <Text style={{ color: active ? ON_ACCENT_TEXT : tc.text, fontSize: 13, fontWeight: fontWeights.bold } as any}>{d}</Text>
                                                    </TouchableOpacity>
                                                );
                                            })}
                                        </View>

                                        {/* iOS Safari quirk: <input type="date"/"time"> ignore
                                            CSS width and force their intrinsic ~150px width, which
                                            overflows narrow flex columns and makes the «Дата»
                                            field overlap «Время» on mobile. Switching to a
                                            column stack on mobile (each field full-width) avoids
                                            the collision entirely. WebkitAppearance: 'none' on
                                            the inputs is the belt-and-suspenders bit — even if
                                            a future Safari respects the width, the native
                                            picker chrome won't bloat past the container. */}
                                        <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 12, marginBottom: 8 }}>
                                            <View style={{ flex: isMobile ? undefined : 1, width: isMobile ? '100%' : undefined, minWidth: 0 }}>
                                                <Text style={fieldLabel(tc.text) as any}>Дата</Text>
                                                {Platform.OS === 'web' ? (
                                                    // padding 18px→12px / font 17→16: en-US locales render «07/19/2026» +
                                                    // the picker icon, which clipped at the old paddings («07/19/20…»).
                                                    <input type="date" min={todayISO()} value={date} onChange={(e: any) => setDate(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 14, padding: '17px 12px', fontSize: 16, fontWeight: 600, fontFamily: 'inherit', color: tc.text, backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#FFF', cursor: 'pointer', WebkitAppearance: 'none', appearance: 'none' } as any} />
                                                ) : (
                                                    <TextInput value={date} onChangeText={setDate} style={inputStyle(isDark, tc) as any} />
                                                )}
                                            </View>
                                            {/* 130→150px: 12-hour locales render «01:00 AM» + the clock icon,
                                                which clipped to «01:00 A» in the old 130px column. */}
                                            <View style={{ width: isMobile ? '100%' : 150, minWidth: 0 }}>
                                                <Text style={fieldLabel(tc.text) as any}>Время</Text>
                                                {Platform.OS === 'web' ? (
                                                    <input type="time" value={time} onChange={(e: any) => setTime(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 14, padding: '17px 12px', fontSize: 16, fontWeight: 600, fontFamily: 'inherit', color: tc.text, backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#FFF', cursor: 'pointer', WebkitAppearance: 'none', appearance: 'none' } as any} />
                                                ) : (
                                                    <TextInput value={time} onChangeText={setTime} style={inputStyle(isDark, tc) as any} />
                                                )}
                                            </View>
                                            <View style={{ width: isMobile ? '100%' : 110, minWidth: 0 }}>
                                                <Text style={fieldLabel(tc.text) as any}>Длительность</Text>
                                                <TextInput value={duration} onChangeText={(v) => setDuration(v.replace(/[^0-9]/g, ''))} keyboardType="numeric" style={[inputStyle(isDark, tc), { paddingVertical: 18, paddingHorizontal: 18, fontSize: 17, fontWeight: '600', borderRadius: 14 }] as any} />
                                            </View>
                                        </View>

                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 18 } as any}>
                                            <Ionicons name="time-outline" size={13} color={tc.textMuted} />
                                            <Text style={{ fontSize: 12, color: tc.textMuted, lineHeight: 16 } as any}>
                                                Live-сессию можно начать минимум через {MIN_LIVE_LEAD_MINUTES} минут — чтобы гость успел открыть ссылку и подготовить камеру.
                                            </Text>
                                        </View>

                                        <TouchableOpacity onPress={() => setIsRegular(v => !v)} activeOpacity={0.8} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, borderRadius: 12, backgroundColor: isRegular ? 'rgba(233, 130, 69,0.08)' : 'transparent', borderWidth: 1, borderColor: isRegular ? 'rgba(233, 130, 69,0.25)' : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'), marginBottom: 12, ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}>
                                            <View style={{ width: 22, height: 22, borderRadius: 6, alignItems: 'center', justifyContent: 'center', backgroundColor: isRegular ? ACCENT : 'transparent', borderWidth: 2, borderColor: isRegular ? ACCENT : tc.textMuted } as any}>
                                                {isRegular && <Ionicons name="checkmark" size={14} color={ON_ACCENT_TEXT} />}
                                            </View>
                                            <Text style={{ flex: 1, fontSize: 14, fontWeight: '600', color: tc.text } as any}>Регулярное событие — повторяется каждую неделю</Text>
                                        </TouchableOpacity>

                                        {/* AMB-24 — Linked library practice. Optional attachment that
                                            auto-fills title / cover / duration on first pick. When set,
                                            renders as a chip with cover + title + remove (×). When
                                            empty, a single button opens the picker (inline panel below). */}
                                        <View style={{ marginBottom: 14 } as any}>
                                            <Text style={fieldLabel(tc.text) as any}>Live-практика</Text>
                                            <Text style={{ fontSize: 12, color: tc.textMuted, marginTop: -4, marginBottom: 8, lineHeight: 17 } as any}>
                                                Привяжите практику из вашей библиотеки — заголовок, обложка и длительность подставятся автоматически.
                                            </Text>
                                            {linkedPractice ? (
                                                <View style={{
                                                    flexDirection: 'row', alignItems: 'center', gap: 10,
                                                    padding: 10, borderRadius: 12,
                                                    backgroundColor: 'rgba(232,93,4,0.06)',
                                                    borderWidth: 1, borderColor: 'rgba(232,93,4,0.30)',
                                                    marginBottom: 4,
                                                } as any}>
                                                    {linkedPractice.coverImageUrl ? (
                                                        <Image source={{ uri: getFileUrl(linkedPractice.coverImageUrl) || undefined }} style={{ width: 44, height: 44, borderRadius: 8 } as any} />
                                                    ) : (
                                                        <View style={{ width: 44, height: 44, borderRadius: 8, backgroundColor: 'rgba(232,93,4,0.18)', alignItems: 'center', justifyContent: 'center' } as any}>
                                                            <Ionicons name="play-circle-outline" size={22} color={ACCENT} />
                                                        </View>
                                                    )}
                                                    <View style={{ flex: 1 } as any}>
                                                        <Text numberOfLines={1} style={{ fontSize: 14, fontWeight: fontWeights.bold, color: tc.text } as any}>
                                                            {linkedPractice.title}
                                                        </Text>
                                                        <Text style={{ fontSize: 12, color: tc.textMuted } as any}>
                                                            {linkedPractice.durationMinutes ? `${linkedPractice.durationMinutes} мин` : 'Длительность не задана'}
                                                            {linkedPractice.category ? ` · ${linkedPractice.category}` : ''}
                                                        </Text>
                                                    </View>
                                                    <TouchableOpacity onPress={() => setLinkedPractice(null)} style={{ width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' } as any}>
                                                        <Ionicons name="close" size={18} color={tc.textMuted} />
                                                    </TouchableOpacity>
                                                </View>
                                            ) : (
                                                <TouchableOpacity
                                                    onPress={() => setPracticePickerOpen(v => !v)}
                                                    activeOpacity={0.85}
                                                    style={{
                                                        flexDirection: 'row', alignItems: 'center', gap: 10,
                                                        paddingVertical: 14, paddingHorizontal: 14,
                                                        borderRadius: 12,
                                                        backgroundColor: practicePickerOpen ? 'rgba(232,93,4,0.06)' : 'transparent',
                                                        borderWidth: 1, borderColor: practicePickerOpen ? 'rgba(232,93,4,0.30)' : (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'),
                                                        ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                                                    } as any}
                                                >
                                                    <Ionicons name="library-outline" size={18} color={ACCENT} />
                                                    <Text style={{ flex: 1, fontSize: 14, fontWeight: '600', color: tc.text } as any}>
                                                        {practicePickerOpen ? 'Скрыть библиотеку' : 'Выбрать практику из библиотеки'}
                                                    </Text>
                                                    <Ionicons name={practicePickerOpen ? 'chevron-up' : 'chevron-down'} size={16} color={tc.textMuted} />
                                                </TouchableOpacity>
                                            )}
                                            {practicePickerOpen && !linkedPractice && (
                                                <View style={{ marginTop: 10 } as any}>
                                                    <TextInput
                                                        value={practiceSearch}
                                                        onChangeText={setPracticeSearch}
                                                        placeholder="Поиск по названию..."
                                                        placeholderTextColor={tc.textMuted}
                                                        style={[inputStyle(isDark, tc), { marginBottom: 8 }] as any}
                                                    />
                                                    {practiceLibraryLoading ? (
                                                        <ActivityIndicator size="small" color={ACCENT} style={{ marginVertical: 12 } as any} />
                                                    ) : (() => {
                                                        const query = practiceSearch.trim().toLowerCase();
                                                        const filtered = query
                                                            ? practiceLibrary.filter(p => (p.title || '').toLowerCase().includes(query))
                                                            : practiceLibrary;
                                                        if (filtered.length === 0) {
                                                            return (
                                                                <Text style={{ fontSize: 13, color: tc.textMuted, paddingVertical: 12, textAlign: 'center' } as any}>
                                                                    {practiceLibrary.length === 0
                                                                        ? 'В вашей библиотеке пока нет опубликованных практик.'
                                                                        : 'Ничего не найдено по запросу.'}
                                                                </Text>
                                                            );
                                                        }
                                                        return (
                                                            <View style={{ maxHeight: 260, borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', borderRadius: 12, overflow: 'hidden' } as any}>
                                                                <ScrollView style={{ maxHeight: 260 } as any}>
                                                                    {filtered.map((p, idx) => (
                                                                        <TouchableOpacity
                                                                            key={p.id}
                                                                            activeOpacity={0.85}
                                                                            onPress={() => { setLinkedPractice(p); setPracticePickerOpen(false); }}
                                                                            style={{
                                                                                flexDirection: 'row', alignItems: 'center', gap: 10,
                                                                                padding: 10,
                                                                                borderBottomWidth: idx === filtered.length - 1 ? 0 : 1,
                                                                                borderBottomColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                                                                ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                                                                            } as any}
                                                                        >
                                                                            {p.coverImageUrl ? (
                                                                                <Image source={{ uri: getFileUrl(p.coverImageUrl) || undefined }} style={{ width: 44, height: 44, borderRadius: 8 } as any} />
                                                                            ) : (
                                                                                <View style={{ width: 44, height: 44, borderRadius: 8, backgroundColor: 'rgba(232,93,4,0.18)', alignItems: 'center', justifyContent: 'center' } as any}>
                                                                                    <Ionicons name="play-circle-outline" size={22} color={ACCENT} />
                                                                                </View>
                                                                            )}
                                                                            <View style={{ flex: 1 } as any}>
                                                                                <Text numberOfLines={1} style={{ fontSize: 13, fontWeight: '600', color: tc.text } as any}>{p.title}</Text>
                                                                                <Text style={{ fontSize: 11, color: tc.textMuted } as any}>
                                                                                    {p.durationMinutes ? `${p.durationMinutes} мин` : '—'}
                                                                                    {p.category ? ` · ${p.category}` : ''}
                                                                                </Text>
                                                                            </View>
                                                                        </TouchableOpacity>
                                                                    ))}
                                                                </ScrollView>
                                                            </View>
                                                        );
                                                    })()}
                                                </View>
                                            )}
                                        </View>

                                        {/* ─── Visibility selector (CORE-505) — «Кто увидит Live-сессию».
                                            Mirrors the native LiveWizard 3-level tier. Shown on
                                            create/edit; disabled once the live is already running. ─── */}
                                        <View style={{ marginBottom: 14 } as any}>
                                            <Text style={fieldLabel(tc.text) as any}>Кто увидит Live-сессию</Text>
                                            {/* CORE-879 — у Live-сессии из платной программы
                                                четвёртый пункт: см. liveAudienceOptions. */}
                                            {liveAudienceOptions(existingLive?.visibility).map((o) => {
                                                const on = visibility === o.key;
                                                return (
                                                    <TouchableOpacity
                                                        key={o.key}
                                                        disabled={formatControlsDisabled}
                                                        activeOpacity={0.85}
                                                        onPress={() => setVisibility(o.key)}
                                                        style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, borderWidth: 1, borderColor: on ? ACCENT : (isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)'), backgroundColor: on ? (isDark ? 'rgba(233, 130, 69,0.10)' : 'rgba(233, 130, 69,0.06)') : 'transparent', marginBottom: 8, opacity: formatControlsDisabled ? 0.5 : 1, ...(Platform.OS === 'web' && { cursor: formatControlsDisabled ? 'not-allowed' : 'pointer' } as any) } as any}
                                                    >
                                                        <View style={{ width: 20, height: 20, borderRadius: 10, borderWidth: 2, borderColor: on ? ACCENT : tc.textMuted, alignItems: 'center', justifyContent: 'center' } as any}>
                                                            {on && <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: ACCENT } as any} />}
                                                        </View>
                                                        <Ionicons name={o.icon as any} size={18} color={on ? ACCENT : tc.textMuted} />
                                                        <View style={{ flex: 1 } as any}>
                                                            <Text style={{ color: tc.text, fontWeight: fontWeights.bold, fontSize: 14 } as any}>{o.title}</Text>
                                                            <Text style={{ color: tc.textMuted, fontSize: 12, marginTop: 2, lineHeight: 16 } as any}>{o.desc}</Text>
                                                        </View>
                                                    </TouchableOpacity>
                                                );
                                            })}
                                            {formatControlsDisabled && (
                                                <Text style={{ fontSize: 12, color: tc.textMuted, marginTop: 2 } as any}>
                                                    Live-сессия уже идёт — видимость менять нельзя.
                                                </Text>
                                            )}
                                        </View>

                                        {/* ─── Format selector (spec D-1, D-2, D-9) ─── */}
                                        {!formatControlsHidden && (
                                        <View style={{ marginBottom: 14 } as any}>
                                            <Text style={fieldLabel(tc.text) as any}>Формат</Text>
                                            <View style={{ flexDirection: 'row', borderRadius: 12, overflow: 'hidden', borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)', marginBottom: 10 } as any}>
                                                {(['solo', 'with-guests'] as const).map((m) => {
                                                    const active = liveFormat === m;
                                                    return (
                                                        <TouchableOpacity
                                                            key={m}
                                                            disabled={formatControlsDisabled}
                                                            onPress={() => {
                                                                setLiveFormat(m);
                                                                if (m === 'with-guests') setGuestCount(1); // spec §6: reset rule
                                                            }}
                                                            activeOpacity={0.85}
                                                            style={{ flex: 1, paddingVertical: 12, alignItems: 'center', backgroundColor: active ? ACCENT : 'transparent', opacity: formatControlsDisabled ? 0.5 : 1, ...(Platform.OS === 'web' && { cursor: formatControlsDisabled ? 'not-allowed' : 'pointer' } as any) } as any}
                                                        >
                                                            <Text style={{ color: active ? ON_ACCENT_TEXT : tc.text, fontSize: 13, fontWeight: fontWeights.bold } as any}>
                                                                {m === 'solo' ? 'Соло' : 'С гостями'}
                                                            </Text>
                                                        </TouchableOpacity>
                                                    );
                                                })}
                                            </View>

                                            {/* Picker «1/2/3/4 гостей» убран по решению продакта 2026-05-07
                                                — формат «с гостем» теперь всегда означает «мастер + 1 соведущий»
                                                (interview/dialog vibe вместо panel discussion). Multi-cohost
                                                код (SpeakerGrid layouts 3/4/5) сохранён под будущий Panel Mode
                                                — см. SCRUM-38 (won't-do) для контекста решения. */}

                                            {formatControlsDisabled && (
                                                <Text style={{ fontSize: 12, color: tc.textMuted, marginTop: 6 } as any}>
                                                    Live-сессия уже идёт — формат менять нельзя.
                                                </Text>
                                            )}
                                        </View>
                                        )}

                                        {/* Adaptive info banner — copy reflects format choice. Co-host
                                            invites are issued from the success step (with-guests) or
                                            the broadcast preview room (MasterLivePublisher). */}
                                        <View style={{
                                            flexDirection: 'row', alignItems: 'flex-start', gap: 10,
                                            padding: 12, borderRadius: 12,
                                            backgroundColor: isDark ? 'rgba(56,161,105,0.08)' : 'rgba(56,161,105,0.06)',
                                            borderWidth: 1, borderColor: 'rgba(56,161,105,0.25)',
                                        } as any}>
                                            <Ionicons name="globe-outline" size={16} color="#22C55E" style={{ marginTop: 1 } as any} />
                                            <Text style={{ flex: 1, fontSize: 12, color: tc.text, opacity: 0.85, lineHeight: 17 } as any}>
                                                {liveFormat === 'solo'
                                                    ? (cohostsEnabled
                                                        ? 'Live-сессия пройдёт соло. Зрители смогут видеть и слышать вас, общаться в чате. Если по ходу Live-сессии захотите подключить соведущего — кнопка «Пригласить» доступна прямо из студии.'
                                                        : 'Live-сессия пройдёт соло. Зрители смогут видеть и слышать вас, общаться в чате.')
                                                    : 'Live-сессия с одним соведущим (всего 2 спикера в кадре). После публикации появится ссылка — отправьте её тому, кого приглашаете. По ссылке гость зайдёт с микрофоном и камерой. Зрители видят и слышат обоих спикеров, общаются в чате.'}
                                            </Text>
                                        </View>
                                    </View>

                                    {/* ── RIGHT COLUMN ── */}
                                    <View style={{ flex: 1, minWidth: 300 } as any}>
                                        <Text style={fieldLabel(tc.text) as any}>Обложка</Text>
                                        <View style={{ width: '100%', aspectRatio: 16 / 9, borderRadius: 14, overflow: 'hidden', backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', marginBottom: 12, alignItems: 'center', justifyContent: 'center' } as any}>
                                            {coverPreview ? (
                                                <Image source={{ uri: coverPreview }} style={{ width: '100%', height: '100%' } as any} />
                                            ) : (
                                                <View style={{ alignItems: 'center', gap: 8 }}>
                                                    <Ionicons name="image-outline" size={32} color={tc.textMuted} />
                                                    <Text style={{ fontSize: 14, color: tc.textMuted } as any}>Выберите обложку ниже</Text>
                                                </View>
                                            )}
                                        </View>

                                        {coversLoading ? (
                                            <View style={{ paddingVertical: 16, alignItems: 'center' }}>
                                                <ActivityIndicator size="small" color={ACCENT} />
                                            </View>
                                        ) : unsplashCovers.length > 0 ? (
                                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                                                {unsplashCovers.slice(0, 8).map((img) => {
                                                    const selected = coverPreview === unsplashPhotoUrl(img.id, 'regular');
                                                    return (
                                                        <TouchableOpacity
                                                            key={img.id}
                                                            testID={`live-stream-cover-${img.id}`}
                                                            onPress={() => handlePickUnsplash(img)}
                                                            disabled={!!pickingCoverId}
                                                            activeOpacity={0.85}
                                                            style={{ width: 'calc(25% - 6px)' as any, aspectRatio: 16 / 9, borderRadius: 8, overflow: 'hidden', borderWidth: 2, borderColor: selected ? ACCENT : 'transparent', ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}
                                                        >
                                                            <Image source={{ uri: unsplashPhotoUrl(img.id, 'small') }} style={{ width: '100%', height: '100%' } as any} />
                                                            {pickingCoverId === img.id && (
                                                                <View style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.35)' } as any}>
                                                                    <ActivityIndicator size="small" color="#fff" />
                                                                </View>
                                                            )}
                                                        </TouchableOpacity>
                                                    );
                                                })}
                                            </View>
                                        ) : null}
                                        {coversFailed && !coversLoading && (
                                            <View testID="live-stream-covers-failed" style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 10 } as any}>
                                                <Text style={{ color: '#EF4444', fontSize: 13 } as any}>Подборка обложек не загрузилась.</Text>
                                                <TouchableOpacity testID="live-stream-covers-retry" accessibilityRole="button" onPress={loadCovers} activeOpacity={0.85}>
                                                    <Text style={{ color: ACCENT, fontSize: 13, fontWeight: fontWeights.bold } as any}>Повторить</Text>
                                                </TouchableOpacity>
                                            </View>
                                        )}
                                        {coverError && (
                                            <View testID="live-stream-cover-error" style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 10 } as any}>
                                                <Text style={{ color: '#EF4444', fontSize: 13, flexShrink: 1 } as any}>{coverError}</Text>
                                                {coverRetry && (
                                                    <TouchableOpacity testID="live-stream-cover-retry" accessibilityRole="button" onPress={coverRetry.run} activeOpacity={0.85}>
                                                        <Text style={{ color: ACCENT, fontSize: 13, fontWeight: fontWeights.bold } as any}>Повторить</Text>
                                                    </TouchableOpacity>
                                                )}
                                            </View>
                                        )}

                                        <TouchableOpacity onPress={handlePickCover} activeOpacity={0.85} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12, paddingHorizontal: 14, borderRadius: 12, backgroundColor: isDark ? 'rgba(233, 130, 69,0.08)' : 'rgba(233, 130, 69,0.06)', borderWidth: 1, borderColor: 'rgba(233, 130, 69,0.25)', borderStyle: 'dashed', marginBottom: 6, ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}>
                                            <Ionicons name="cloud-upload-outline" size={18} color={ACCENT} />
                                            <Text style={{ flex: 1, fontSize: 14, color: ACCENT, fontWeight: fontWeights.bold } as any}>Загрузить свою картинку</Text>
                                        </TouchableOpacity>
                                    </View>
                                </View>

                            </>
                        )}

                        {step === 'preview' && (
                            <View>
                                <Text style={{ fontSize: 13, color: tc.textMuted, fontWeight: '600', letterSpacing: 1, marginBottom: 12 } as any}>ПРЕДПРОСМОТР АНОНСА</Text>

                                {/* Announcement card preview */}
                                <View style={{ borderRadius: 18, overflow: 'hidden', borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#FFF', marginBottom: 22 } as any}>
                                    <View style={{ width: '100%', aspectRatio: 16 / 9, backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', position: 'relative' } as any}>
                                        {coverPreview ? (
                                            <Image source={{ uri: coverPreview }} style={{ width: '100%', height: '100%' } as any} />
                                        ) : (
                                            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                                                <Ionicons name="image-outline" size={36} color={tc.textMuted} />
                                            </View>
                                        )}
                                        <View style={{ position: 'absolute', top: 12, left: 12, flexDirection: 'row', gap: 6 }}>
                                            <View style={{ backgroundColor: '#DC2626', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999 } as any}>
                                                <Text style={{ color: '#FFF', fontSize: 11, fontWeight: fontWeights.bold, letterSpacing: 0.5 } as any}>LIVE STREAM</Text>
                                            </View>
                                            {isRegular && (
                                                <View style={{ backgroundColor: 'rgba(0,0,0,0.6)', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999 } as any}>
                                                    <Text style={{ color: '#FFF', fontSize: 11, fontWeight: fontWeights.bold } as any}>Каждую неделю</Text>
                                                </View>
                                            )}
                                        </View>
                                    </View>
                                    <View style={{ padding: 18 }}>
                                        <Text style={{ fontSize: 22, fontWeight: fontWeights.bold, color: tc.text, marginBottom: 8, letterSpacing: -0.3 } as any}>{title.trim() || 'Без названия'}</Text>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                                            <Ionicons name="calendar-outline" size={14} color={tc.textMuted} />
                                            <Text style={{ fontSize: 13, color: tc.text, fontWeight: '600' } as any}>{formatPreviewDate(date, time)}</Text>
                                            <Text style={{ fontSize: 13, color: tc.textMuted } as any}>·</Text>
                                            <Text style={{ fontSize: 13, color: tc.text, fontWeight: '600' } as any}>{duration} мин</Text>
                                            <Text style={{ fontSize: 13, color: tc.textMuted } as any}>·</Text>
                                            <Text style={{ fontSize: 13, color: tc.text, fontWeight: '600' } as any}>{masterName || 'Вы'}</Text>
                                        </View>
                                        {description.trim() ? (
                                            <Text style={{ fontSize: 14, color: tc.text, opacity: 0.85, lineHeight: 21 } as any}>{description.trim()}</Text>
                                        ) : (
                                            <Text style={{ fontSize: 13, color: tc.textMuted, fontStyle: 'italic' } as any}>Без описания</Text>
                                        )}
                                    </View>
                                </View>

                                {/* Сообщение в Круг */}
                                <Text style={fieldLabel(tc.text) as any}>Сообщение в вашу ленту</Text>
                                <Text style={{ fontSize: 12, color: tc.textMuted, marginBottom: 8, lineHeight: 17 } as any}>
                                    {feedMessageHint(confirmFeedSuppressed, visibility === 'program')}
                                </Text>
                                <TextInput value={circleMessage} onChangeText={setCircleMessage} placeholder="Дорогие мои, приглашаю вас на…" placeholderTextColor={tc.textMuted} multiline style={[inputStyle(isDark, tc), { minHeight: 70, textAlignVertical: 'top', marginBottom: 18 }] as any} />

                                {/* Disclaimer */}
                                <View style={{ flexDirection: 'row', gap: 10, padding: 14, borderRadius: 12, backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', marginBottom: 18 } as any}>
                                    <Ionicons name="information-circle-outline" size={18} color={tc.textMuted} style={{ marginTop: 1 } as any} />
                                    <Text style={{ flex: 1, fontSize: 12, color: tc.text, opacity: 0.75, lineHeight: 18 } as any}>{PUBLISH_DISCLAIMER}</Text>
                                </View>

                            </View>
                        )}

                        {step === 'success' && (
                            <View style={{ alignItems: 'center', paddingVertical: 12 }}>
                                <View style={{ width: 72, height: 72, borderRadius: 36, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(16,185,129,0.15)', marginBottom: 18 } as any}>
                                    <Ionicons name="checkmark" size={40} color="#10B981" />
                                </View>
                                <Text style={{ fontSize: 24, fontWeight: fontWeights.bold, color: tc.text, textAlign: 'center', marginBottom: 8 } as any}>
                                    {/* CORE-879, 59344 — после правки событие не публикуется заново. */}
                                    {isEdit ? 'Изменения сохранены' : 'Событие опубликовано!'}
                                </Text>
                                <Text style={{ fontSize: 14, color: tc.text, opacity: 0.7, textAlign: 'center', marginBottom: 26, lineHeight: 21, maxWidth: 520 } as any}>
                                    {announceSuccessBody(title, { suppressed: feedSuppressed, cohosts: cohostsEnabled, programOnly: visibility === 'program' })}
                                </Text>

                                {/* Block 1 — guest invite link (with-guests mode only).
                                   AMB-11: also gated behind cohostsEnabled so the
                                   block stays hidden if the flag flipped off
                                   between save and success render. */}
                                {cohostsEnabled && liveFormat === 'with-guests' && createdLive?.id && (
                                    <View style={{ alignSelf: 'stretch', marginBottom: 14, padding: 14, borderRadius: 14, backgroundColor: isDark ? 'rgba(233, 130, 69,0.06)' : 'rgba(233, 130, 69,0.04)', borderWidth: 1, borderColor: 'rgba(233, 130, 69,0.20)' } as any}>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 } as any}>
                                            <Ionicons name="people" size={16} color={ACCENT} />
                                            <Text style={{ fontSize: 14, fontWeight: fontWeights.bold, color: tc.text } as any}>
                                                Ссылка для гостей-соведущих
                                            </Text>
                                        </View>
                                        <Text style={{ fontSize: 12, color: tc.textMuted, marginBottom: 10, lineHeight: 17 } as any}>
                                            Отправьте тому, кого приглашаете в эфир: он зайдёт с микрофоном и камерой и сможет говорить. Лимит — 1 человек.
                                        </Text>
                                        {inviteError ? (
                                            <Text style={{ fontSize: 13, color: '#EF4444' } as any}>{inviteError}</Text>
                                        ) : inviteLink ? (
                                            <>
                                                <TouchableOpacity testID="live-share-invite" onPress={() => openShare('invite')} activeOpacity={0.85} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, borderRadius: 10, backgroundColor: ACCENT, ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}>
                                                    <Ionicons name="share-social-outline" size={16} color={ON_ACCENT_TEXT} />
                                                    <Text style={{ color: ON_ACCENT_TEXT, fontSize: 13, fontWeight: fontWeights.bold } as any}>Поделиться</Text>
                                                </TouchableOpacity>
                                            </>
                                        ) : (
                                            <ActivityIndicator size="small" color={ACCENT} />
                                        )}
                                    </View>
                                )}

                                {/* Block 2 — viewer link (always shown when live exists).
                                    CORE-879 — кроме Live-сессии программы: «отправьте
                                    подписчикам и друзьям» привело бы их на «не найдена». */}
                                {viewerLink && visibility !== 'program' && (
                                    <View style={{ alignSelf: 'stretch', marginBottom: 22, padding: 14, borderRadius: 14, backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' } as any}>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 } as any}>
                                            <Ionicons name="eye-outline" size={16} color={tc.text} />
                                            <Text style={{ fontSize: 14, fontWeight: fontWeights.bold, color: tc.text } as any}>
                                                Ссылка для зрителей
                                            </Text>
                                        </View>
                                        <Text style={{ fontSize: 12, color: tc.textMuted, marginBottom: 10, lineHeight: 17 } as any}>
                                            Отправьте подписчикам и друзьям: они смотрят эфир и пишут в чат, без микрофона и камеры.
                                        </Text>
                                        <TouchableOpacity testID="live-share-viewer" onPress={() => openShare('viewer')} activeOpacity={0.85} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, borderRadius: 10, backgroundColor: ACCENT, ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}>
                                            <Ionicons name="share-social-outline" size={16} color={ON_ACCENT_TEXT} />
                                            <Text style={{ color: ON_ACCENT_TEXT, fontSize: 13, fontWeight: fontWeights.bold } as any}>Поделиться</Text>
                                        </TouchableOpacity>
                                    </View>
                                )}

                                <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', alignSelf: 'stretch' } as any}>
                                    {navigation && createdLive && (
                                        <TouchableOpacity
                                            onPress={() => {
                                                // Edit mode → open the announcement page (no chat focus)
                                                // so the master can verify their changes landed. New-event
                                                // mode → drop straight into the chat to greet early arrivals.
                                                navigation.navigate('LiveDetail', {
                                                    id: createdLive.id,
                                                    ...(isEdit ? {} : { focus: 'chat' }),
                                                });
                                                handleClose();
                                            }}
                                            activeOpacity={0.85}
                                            style={{
                                                flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
                                                height: 52, paddingHorizontal: 22,
                                                backgroundColor: ACCENT, borderRadius: 14,
                                                ...(Platform.OS === 'web' && { cursor: 'pointer', boxShadow: '0 8px 24px rgba(233, 130, 69,0.25)', whiteSpace: 'nowrap' } as any),
                                            } as any}
                                        >
                                            <Ionicons name="eye" size={18} color={ON_ACCENT_TEXT} />
                                            <Text numberOfLines={1} style={{ color: ON_ACCENT_TEXT, fontSize: 14, fontWeight: fontWeights.bold, letterSpacing: 0.3 } as any}>
                                                {isEdit ? 'Просмотр анонса' : 'Открыть анонс'}
                                            </Text>
                                        </TouchableOpacity>
                                    )}
                                    <TouchableOpacity
                                        onPress={handleClose}
                                        activeOpacity={0.85}
                                        style={{
                                            flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                                            height: 52, paddingHorizontal: 26,
                                            borderRadius: 14, borderWidth: 1,
                                            borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)',
                                            ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                                        } as any}
                                    >
                                        <Text numberOfLines={1} style={{ color: tc.text, fontSize: 14, fontWeight: fontWeights.bold, letterSpacing: 0.3 } as any}>
                                            Закрыть
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                            </View>
                        )}
                    </ScrollView>

                    {/* CORE-1169 — липкий футер. Раньше действие было последней
                        строкой ленты в 1472 px: при окне 557 px мастер его не
                        находил вовсе («вёрстка такая, что окно не открывается,
                        надо уменьшить масштаб до 25%») и не мог сохранить правки.
                        Верхняя граница заодно говорит, что выше есть прокрутка. */}
                    {step !== 'success' && (
                        <View style={{ paddingHorizontal: 26, paddingTop: 14, paddingBottom: 18, gap: 10, backgroundColor: CARD_BG, borderTopWidth: 1, borderTopColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' } as any}>
                            {error && (
                                <Text style={{ color: '#EF4444', fontSize: 13 } as any}>{error}</Text>
                            )}

                            {step === 'edit' ? (
                                <>
                                    {!canPreview && directionsRequired && directions.length === 0 && (
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 14, paddingVertical: 10, borderRadius: 10, backgroundColor: isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.07)' } as any}>
                                            <Ionicons name="alert-circle" size={16} color="#EF4444" />
                                            <Text style={{ fontSize: 13, color: '#EF4444', fontWeight: '600' } as any}>Выберите хотя бы одно направление</Text>
                                        </View>
                                    )}

                                    {/* AMB-197 — no `disabled` here. Button stays clickable so
                                        handleGoToPreview can surface a field-specific error;
                                        visual «disabled» state remains via primaryBtn styling. */}
                                    <TouchableOpacity onPress={handleGoToPreview} activeOpacity={0.85} style={[primaryBtn(!canPreview, isDark), { flexDirection: 'row', justifyContent: 'center', gap: 10 }] as any}>
                                        <Ionicons name="eye-outline" size={18} color="#FFF" />
                                        {/* CORE-1169 — «Просмотр анонса» звучал как отдельная
                                            витрина, а это шаг вперёд: за ним и живёт сохранение. */}
                                        <Text style={{ color: '#FFF', fontSize: 15, fontWeight: fontWeights.bold, letterSpacing: 0.5 } as any}>
                                            {isEdit ? 'ДАЛЕЕ · ПРОСМОТР И СОХРАНЕНИЕ' : 'ДАЛЕЕ · ПРОСМОТР АНОНСА'}
                                        </Text>
                                    </TouchableOpacity>
                                </>
                            ) : (
                                <View style={{ flexDirection: 'row', gap: 12 }}>
                                    <TouchableOpacity onPress={() => setStep('edit')} disabled={publishing} activeOpacity={0.85} style={{ paddingVertical: 12, paddingHorizontal: 16, borderRadius: 14, borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)', flexDirection: 'row', alignItems: 'center', gap: 6, ...(Platform.OS === 'web' && { cursor: publishing ? 'not-allowed' : 'pointer' } as any) } as any}>
                                        <Ionicons name="arrow-back" size={14} color={tc.text} />
                                        <Text style={{ color: tc.text, fontSize: 13, fontWeight: fontWeights.bold } as any}>Назад</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity onPress={handlePublish} disabled={publishing} activeOpacity={0.85} style={[orangeBtn(publishing, isDark), { paddingVertical: 12, gap: 8 }] as any}>
                                        {publishing ? (
                                            <ActivityIndicator size="small" color={ON_ACCENT_TEXT} />
                                        ) : (
                                            <>
                                                <Ionicons name={isEdit ? 'checkmark' : 'paper-plane'} size={16} color={ON_ACCENT_TEXT} />
                                                <Text style={{ color: ON_ACCENT_TEXT, fontSize: 13, fontWeight: fontWeights.bold, letterSpacing: 0.3 } as any}>
                                                    {isEdit ? 'СОХРАНИТЬ' : 'ОПУБЛИКОВАТЬ СОБЫТИЕ'}
                                                </Text>
                                            </>
                                        )}
                                    </TouchableOpacity>
                                </View>
                            )}
                        </View>
                    )}
                </View>
            </View>
            {shareFor && shareTexts[shareFor] && (
                <ShareModal
                    visible
                    onClose={() => setShareFor(null)}
                    url={shareTexts[shareFor]!.url}
                    title={shareTexts[shareFor]!.title}
                    subject={shareTexts[shareFor]!.subject}
                    message={shareTexts[shareFor]!.message}
                    analytics={shareTarget}
                />
            )}
        </Modal>
    );
};


export default LiveStreamModal;
